import React from 'react';
import Immutable from 'immutable';
import { FormCheckbox } from '@uncinc/uncinc-react-kitchen-sink';
import { useTranslation } from 'react-i18next';

import AddressForm from '../../../../../components/AddressForm';
import AddressTeaser from '../../../../../components/AddressOverview/AddressTeaser';
import AddressSelectOverlay from '../AddressSelectOverlay';
import type { CheckoutFormValues } from '../../types/checkout.types';

/**
 * Delivery Address Section Component
 *
 * Renders form fields for selecting and managing delivery addresses. Allows
 * users to use default billing address or select/create a custom delivery
 * address. Shows address selection overlay and address form based on state.
 *
 * @component
 */

interface DeliveryAddressSectionProps {
  /** Currently selected address index, or null if none selected */
  selectedAddressIndex: number | null;
  /** Form values containing delivery address data */
  values: CheckoutFormValues;
  /** Default address index from user profile */
  defaultAddressIndex: number | null;
  /** Callback to update the selected address index */
  setSelectedAddressIndex: (index: number) => void;
  /** Immutable.List of user's saved addresses */
  // TODO: Replace 'any' with proper address type definition
  addressesRaw: Immutable.List<any>;
  /** React ref to the checkout form */
  // TODO: Replace 'any' with proper Formik ref type when available
  formRef: React.RefObject<any>;
  /** Callback to populate form with selected address */
  // TODO: Replace 'any' with proper address type definition
  selectAddress: (ref: React.RefObject<any>, address: any) => void;
}

const DeliveryAddressSection: React.FC<DeliveryAddressSectionProps> = ({
  selectedAddressIndex,
  values,
  defaultAddressIndex,
  setSelectedAddressIndex,
  addressesRaw,
  formRef,
  selectAddress,
}) => {
  const { t } = useTranslation();

  return (
    <fieldset>
      <legend>{t('address.delivery_address')}</legend>
      {selectedAddressIndex === null && (
        <FormCheckbox
          fieldName="delivery_differs_from_billing"
          title={t('address.default_address_select')}
        />
      )}
      <div
        className={
          values.delivery_differs_from_billing === false ? 'hidden' : ''
        }
      >
        {selectedAddressIndex === null && (
          <AddressSelectOverlay
            formRef={formRef}
            setSelectedAddressIndex={setSelectedAddressIndex}
            selectAddress={selectAddress}
            selectedAddressIndex={selectedAddressIndex}
          />
        )}

        {selectedAddressIndex === null && (
          <AddressForm
            hideTitle
            fieldsOnly
            showActions={false}
            allowSetDefault={false}
            allowSetDefaultBilling={false}
          />
        )}
        {selectedAddressIndex !== null && (
          <div>
            {/* Show the teaser of the selected address with its default badge */}
            <AddressTeaser
              address={addressesRaw.get(selectedAddressIndex)}
              isDefault={
                Number(selectedAddressIndex) === Number(defaultAddressIndex)
              }
            />

            {/* Render the address select overlay */}
            <AddressSelectOverlay
              formRef={formRef}
              selectedAddressIndex={selectedAddressIndex}
              setSelectedAddressIndex={setSelectedAddressIndex}
              selectAddress={selectAddress}
            />
          </div>
        )}
      </div>
    </fieldset>
  );
};

export default DeliveryAddressSection;
